// Plays a TTS prompt. Manifest-first: if a pre-generated mp3 exists at the
// given URL, play it. Otherwise fall back to browser SpeechSynthesis so the
// interview flow is fully functional before the gate mp3s are generated.
//
// Both paths return when the audio finishes (or errors). Caller awaits.
//
// mp3-vs-fallback is ONE cancellable unit: playMp3 and the SpeechSynthesis
// fallback within a single playPrompt() share a generation token. If the mp3
// times out (a slow first clip) we fall back to synthesis, but the orphaned
// <audio> is HARD-STOPPED (paused + source detached) so a late-arriving clip can
// never start playing alongside the robotic fallback. stopAllAudio() and any
// subsequent playPrompt() advance the generation, neutralizing any in-flight or
// late mp3/synthesis from a prior prompt.

import { lldbg } from "@/lib/lldbg"; // [LLDBG] TEMP

// Watchdog for the SpeechSynthesis fallback: if the utterance hasn't begun
// speaking within this window the engine silently dropped it (the Chromium
// cancel()->speak() race, or the no-gesture case); resolve so the flow proceeds.
const SYNTH_START_TIMEOUT_MS = 1500;
// mp3 metadata load budget before we treat the clip as a miss and fall back to
// SpeechSynthesis. Kept SEPARATE from the synth watchdog so raising it never
// changes fallback-hang detection. The pause + generation guard below — NOT these
// values — is what prevents a late clip from double-playing; the raise is only a
// reduction in unnecessary fallbacks on a slow first fetch.
//
// TWO budgets (Audio Residual A, Phase 1). The FIRST attempt is the one that can
// land on a cold connection: the asset origin sits idle for ~40-60s while
// gate-consent.mp3 (459KB, the ~30-40s consent statement) plays out and the subject
// answers, so the next clip request pays LTE radio promotion + TCP + TLS before a
// single byte arrives. gate-state.mp3 is only 74KB — it cannot spend 3s on
// TRANSFER, so a 3000 miss is reconnect latency, and 3000 sat right on the edge of
// it. That is what sent the state prompt to the robotic voice. 8000 gives the
// reconnect real headroom.
//
// The RETRY deliberately keeps the tight 3000: the first attempt has already paid
// the reconnect by then, so a second miss means something is genuinely wrong and
// the subject should not sit in silence — fail fast to synthesis. Worst case before
// the fallback voice is 8s + 3s, not 8s + 8s.
//
// PHASE 2 SUPERSEDES the reasoning above for GATE clips. The device test came back:
// on cold cellular the element ERRORS almost immediately rather than loading slowly,
// so no budget was ever going to be the fix. Gate clips now play from a preloaded
// blob: URL and never touch the network (see the blob cache above). These budgets
// stay as insurance for the NETWORK path, which is still live for the 25 non-gate
// clips and for any gate clip whose blob is missing.
const MP3_LOAD_TIMEOUT_FIRST_MS = 8000;
const MP3_LOAD_TIMEOUT_RETRY_MS = 3000;

// iOS plays Web Audio output audibly quieter than HTMLAudioElement at the same
// measured loudness — the intro uses an element, the gate clips use Web Audio,
// so the gates sound softer despite even LUFS. This lifts them to match; tune by
// ear. Ceiling ~1.7x: gate-dob peaks at -4.8 dBTP, so above that risks clipping.
const GATE_WEB_AUDIO_GAIN = 1.5;

// ---- Gate-clip blob cache (Audio Residual A, Phase 2) ----------------------
// The device test settled Phase 1: on a cold cellular connection the media element
// does NOT load slowly — it ERRORS almost immediately. No keepalive and no load
// budget can rescue that; the only fix is for playback never to touch the network.
//
// The preload (InterviewLanding.onBegin) already fetches every clip. For the three
// GATE clips it now also keeps the bytes as a Blob and registers an object URL here.
// playPrompt hands that blob: URL to playMp3 instead of the origin URL, so the
// element reads from memory and cannot fail on a cold radio.
//
// Deliberately a URL -> bytes Map, NOT an element cache: this leaves playMp3
// byte-for-byte, and leaves hardStop, the generation/supersede logic, the retry, and
// the synthesis fallback untouched. An element cache would have forced a hardStop
// split — hardStop detaches src, which would destroy a cached element — and that is
// the load-bearing machinery preventing a late clip from talking over the fallback.
//
// The entry keeps BOTH the Blob and its object URL. The object URL feeds the element
// path; the Blob feeds decodeAudioData on the Web Audio path below without a
// round-trip back through fetch(blob:). createObjectURL already pins the Blob for as
// long as the URL is live, so holding the reference costs no extra memory.
// retainDecoded: whether the decoded AudioBuffer is CACHED across plays (gate
// clips, which replay across gate retries and are few + short) or decoded fresh
// each play and released (question prompts — 23 of them; retaining all their
// decoded PCM would hold tens of MB for the whole interview on an older phone).
type GateClipEntry = { blob: Blob; objectUrl: string; retainDecoded: boolean };
const gateClipBlobs = new Map<string, GateClipEntry>();

// Register a fetched clip by its ORIGIN url (the same string playPrompt is
// called with). Revokes any prior object URL for that clip so a double registration
// cannot leak. retainDecoded defaults true — gate clips keep their exact prior
// behavior; callers pass false for clips that should decode-on-play only.
export const cacheGateClipBlob = (url: string, blob: Blob, retainDecoded = true): void => {
  const previous = gateClipBlobs.get(url);
  if (previous) URL.revokeObjectURL(previous.objectUrl);
  gateClipBlobs.set(url, { blob, objectUrl: URL.createObjectURL(blob), retainDecoded });
};

// The blob: URL for a clip, or null if never registered / already revoked. A null is
// NOT an error — the caller falls back to the origin URL, which keeps its own retry
// and synthesis ladder beneath it.
//
// Only GATE clips are ever registered, so a non-null result also answers "is this a
// gate clip?" — which is how playPrompt decides whether to try Web Audio.
export const getCachedClipUrl = (url: string): string | null =>
  gateClipBlobs.get(url)?.objectUrl ?? null;

// Release every registered object URL and drop the decoded buffers. Called on
// interview unmount; without it the blobs stay pinned for the document's lifetime.
export const revokeGateClipBlobs = (): void => {
  for (const entry of gateClipBlobs.values()) {
    URL.revokeObjectURL(entry.objectUrl);
  }
  gateClipBlobs.clear();
  gateClipBuffers.clear();
};

// ---- Web Audio playback for gate clips (Audio Residual A — the autoplay fix) --
// PROVEN on-device: HTMLAudioElement.play() outside a user gesture throws
// NotAllowedError on iOS — for the blob: source exactly as for the network one,
// because the block is on the ELEMENT, not the source. An AudioBufferSourceNode on
// an AudioContext that was resumed inside a gesture plays gesture-lessly (console
// oscillator: audible, ctx.state "running"). Web Audio's unlock is per-CONTEXT, and
// InterviewLanding primes exactly one context inside the Begin tap.
//
// So the gate clips route through Web Audio, and every existing layer stays beneath
// them untouched as fallback.
let gateAudioContext: AudioContext | null = null;

// Set from InterviewLanding once the Begin-tap context exists, and nulled when that
// context is closed on unmount. A module-level setter rather than a playPrompt
// parameter: all three playPrompt callers already hold the context, so threading it
// through their call sites would churn three components to say the same thing once.
export const setGateAudioContext = (ctx: AudioContext | null): void => {
  gateAudioContext = ctx;
};

// Decoded gate clips, keyed by ORIGIN url. Decode is the expensive part, so it
// happens once per clip and every replay (retries, re-records) reuses the buffer.
const gateClipBuffers = new Map<string, AudioBuffer>();

// The AudioBufferSourceNode currently playing, tracked so stopAllAudio can stop it —
// the Web Audio mirror of currentMp3.
let currentSource: AudioBufferSourceNode | null = null;

// The mp3 currently playing (if any), tracked module-level so stopAllAudio() can
// pause it — playMp3's Audio was previously local/untracked and unstoppable.
let currentMp3: HTMLAudioElement | null = null;

// Monotonic token identifying the CURRENT prompt. Bumped by stopAllAudio() (which
// every playPrompt() calls first). A playMp3/synthesis call captures the token it
// began under; if the live token has since moved on, that call's late canplay/play
// is neutralized instead of producing sound. This is what makes mp3-vs-fallback
// (and prompt-vs-prompt) a single cancellable unit.
let playGeneration = 0;

// Pause + fully detach a clip so a pending/late load cannot begin playing. Used on
// every playMp3 reject path and by stopAllAudio().
const hardStop = (audio: HTMLAudioElement) => {
  try {
    audio.pause();
    audio.src = "";
    audio.load();
  } catch {
    // already detached / unsupported — best effort.
  }
};

// Plays a GATE clip through Web Audio. Returns "played" when the clip ran to
// completion (or was deliberately stopped), "miss" when this path cannot serve the
// clip and the caller should fall through to the element ladder.
//
// A "miss" is never fatal and never surfaces to the subject: every reason for one
// (no context, closed context, unregistered blob, undecodable bytes) leaves the
// existing playMp3 -> retry -> synthesis ladder fully intact underneath.
//
// decodeAudioData rejecting is a CLEAN miss — it is how truncated or non-audio bytes
// (e.g. a blob registered from a partial response) get caught here and handed down.
type WebAudioResult = "played" | "miss";

const playViaWebAudio = async (
  url: string,
  generation: number,
): Promise<WebAudioResult> => {
  const ctx = gateAudioContext;
  if (!ctx || ctx.state === "closed") {
    return "miss";
  }
  try {
    // Idempotent resume: a no-op on a running context, and permitted WITHOUT a fresh
    // gesture because the context was already unlocked inside the Begin tap. Guards
    // the backgrounded/interrupted case. Mirrors the same guard recordWithSilenceDetection
    // already relies on (audioRecorder.ts) — that analyser works after the ~30-40s
    // consent statement, which is direct evidence this context survives the idle.
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    let buffer = gateClipBuffers.get(url);
    if (!buffer) {
      const entry = gateClipBlobs.get(url);
      if (!entry) {
        return "miss";
      }
      // decodeAudioData detaches the ArrayBuffer it is handed, which is fine: we
      // take a fresh one off the Blob and cache the decoded AudioBuffer, so no
      // buffer is ever decoded twice.
      buffer = await ctx.decodeAudioData(await entry.blob.arrayBuffer());
      // Only cache the decoded buffer for retain-eligible clips (gate clips). A
      // question clip is left uncached, so it is decoded fresh on the rare replay
      // and its (large) decoded PCM is released rather than held for the session.
      if (entry.retainDecoded) gateClipBuffers.set(url, buffer);
    }

    // Superseded while we were resuming/decoding: do NOT start making sound.
    if (generation !== playGeneration) {
      return "miss";
    }

    return await new Promise<WebAudioResult>((resolve) => {
      const source = ctx.createBufferSource();
      source.buffer = buffer!;
      // source -> gain -> destination, so the gate clips land at the same perceived
      // level as the element-played intro (see GATE_WEB_AUDIO_GAIN).
      const gain = ctx.createGain();
      gain.gain.value = GATE_WEB_AUDIO_GAIN;
      source.connect(gain);
      gain.connect(ctx.destination);
      currentSource = source;
      lldbg("promptAudio:attempt", { usingWebAudioPath: true, audioContextState: ctx.state }); // [LLDBG] TEMP
      // Fires on natural end AND on an explicit stop() from stopAllAudio, so a
      // superseded clip resolves here rather than hanging the caller. Resolving
      // "played" on a stop is correct: playPrompt returns without starting
      // synthesis, which is exactly what a supersede should do.
      source.onended = () => {
        if (currentSource === source) currentSource = null;
        try {
          source.disconnect();
          // Also drop the gain node — it is the one still wired to destination, so
          // leaving it attached would accumulate a dangling node per clip played.
          gain.disconnect();
        } catch {
          // already disconnected — best effort.
        }
        resolve("played");
      };
      source.start();
      lldbg("promptAudio:playing", { usingWebAudioPath: true, audioContextState: ctx.state }); // [LLDBG] TEMP
    });
  } catch {
    return "miss";
  }
};

const playMp3 = (
  url: string,
  generation: number,
  loadTimeoutMs: number,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const audio = new Audio(url);
    currentMp3 = audio;
    let settled = false;
    let timeoutId = 0;

    const cleanup = () => {
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onErr);
      audio.removeEventListener("canplay", onCanPlay);
      window.clearTimeout(timeoutId);
      if (currentMp3 === audio) currentMp3 = null;
    };
    // Reject paths (timeout, load error, play() rejection, superseded): pause and
    // clear the source BEFORE releasing the handle, so a clip that finishes
    // loading after we've given up can never start playing alongside the fallback.
    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      hardStop(audio);
      reject(err);
    };
    const onEnd = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const onErr = () => fail(new Error(`mp3 load failed: ${url}`));
    const onCanPlay = () => {
      window.clearTimeout(timeoutId);
      // A newer prompt or stopAllAudio() ran while this clip was still loading:
      // neutralize it rather than letting it play late.
      if (generation !== playGeneration) {
        fail(new Error(`mp3 superseded: ${url}`));
      }
    };

    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onErr);
    audio.addEventListener("canplay", onCanPlay);

    // If metadata doesn't load within the budget, treat as a miss and let the
    // SpeechSynthesis fallback take over — hard-stopping the orphan so it cannot
    // surface later.
    timeoutId = window.setTimeout(() => {
      if (audio.readyState < 2) {
        fail(new Error(`mp3 load timeout: ${url}`));
      }
    }, loadTimeoutMs);

    // [LLDBG] TEMP — actual element attempt (usingElementPath). srcIsBlob tells
    // whether this attempt used the cached blob or the network URL; no URL is logged.
    lldbg("promptAudio:attempt", { usingElementPath: true, srcIsBlob: url.startsWith("blob:"), muted: audio.muted, volume: audio.volume, currentTime: audio.currentTime, readyState: audio.readyState, networkState: audio.networkState }); // [LLDBG] TEMP
    // Two-argument then(onPlaying, onRejected) preserves the original .catch(fail)
    // semantics exactly: the reject handler only handles play() rejection, and a
    // throw in the diagnostic success handler can never route into fail().
    void audio.play().then(
      () => lldbg("promptAudio:playing", { usingElementPath: true, currentTime: audio.currentTime, readyState: audio.readyState, networkState: audio.networkState }), // [LLDBG] TEMP
      (err) => {
        lldbg("promptAudio:rejected", { usingElementPath: true, name: err?.name, message: err?.message, readyState: audio.readyState, networkState: audio.networkState }); // [LLDBG] TEMP
        fail(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });

const speakViaSynthesis = (text: string, generation: number, reason: string): Promise<void> =>
  new Promise((resolve) => {
    if (typeof window.speechSynthesis === "undefined") {
      resolve();
      return;
    }
    // Superseded between playPrompt's fallthrough decision and here: don't start
    // speaking (a newer prompt / stopAllAudio has already taken over).
    if (generation !== playGeneration) {
      resolve();
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1;
    u.volume = 1;

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(startWatchdog);
      resolve();
    };

    // Watchdog mirroring playMp3's load timeout: if the utterance hasn't begun
    // speaking within SYNTH_START_TIMEOUT_MS, the engine silently dropped it
    // (the known Chromium cancel()->speak() race; also the no-gesture case).
    // onstart/onend then never fire, hanging the caller forever — so resolve as
    // if it played silently and let the flow proceed to recording_control.
    // onstart clears the watchdog so a normally-speaking prompt is never cut off.
    const startWatchdog = window.setTimeout(finish, SYNTH_START_TIMEOUT_MS);
    u.onstart = () => window.clearTimeout(startWatchdog);
    u.onend = finish;
    u.onerror = finish;

    // [LLDBG] TEMP — fires only when synthesis ACTUALLY runs (past the undefined /
    // superseded guards above), immediately before the speak path executes.
    lldbg("promptAudio:fallbackSpeech", { reason, audioContextState: gateAudioContext?.state ?? "none" }); // [LLDBG] TEMP
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  });

export const playPrompt = async (text: string, mp3Url?: string): Promise<void> => {
  // Stop anything still playing AND advance the generation so any late clip/synth
  // from a prior prompt is neutralized. Capture the token this prompt runs under.
  stopAllAudio();
  const generation = playGeneration;
  // [LLDBG] TEMP — is the post-skip prompt a cache MISS (no clip -> element ladder
  // is gesture-less-blocked on iOS -> robotic synthesis) or a hit that still sounds
  // wrong? cacheHit is the discriminator; willUseSpeechSynthesis is the upfront
  // predictor (no mp3 at all, or a miss that skips the Web Audio path).
  const cacheHit = mp3Url ? !!getCachedClipUrl(mp3Url) : false; // [LLDBG]
  lldbg("playPrompt", { promptKey: mp3Url ?? "(no-mp3)", cacheHit, willUseSpeechSynthesis: !cacheHit }); // [LLDBG] TEMP
  if (mp3Url) {
    // PHASE 2: prefer the preloaded blob: URL. Playback then reads from memory and
    // never touches the network — which is the entire fix, since a cold-cellular
    // element errors instantly rather than loading slowly. Falls back to the origin
    // URL when the clip was never registered (non-gate clip, preload still in
    // flight, fetch failed) or has been revoked. Resolving the source HERE rather
    // than inside playMp3 keeps playMp3 — and hardStop / stopAllAudio / the
    // generation guard — byte-for-byte unchanged.
    const firstSrc = getCachedClipUrl(mp3Url) ?? mp3Url;

    // AUTOPLAY FIX: GATE clips go through Web Audio FIRST. HTMLAudioElement.play()
    // is blocked here (no user gesture — the gate auto-advances into this), and the
    // block is on the element, so the Phase 2 blob: source did not and could not
    // help. An AudioBufferSourceNode on the gesture-unlocked context is not subject
    // to it.
    //
    // Only gate clips are ever registered in the blob cache, so getCachedClipUrl()
    // doubles as the "is this a gate clip?" test: question clips and
    // FinalConsentRecording's no-mp3 prompts never enter this branch and behave
    // exactly as before.
    //
    // Any miss falls straight through to the untouched element ladder below.
    if (getCachedClipUrl(mp3Url)) {
      const webAudio = await playViaWebAudio(mp3Url, generation);
      if (webAudio === "played") return;
      // Superseded during the Web Audio attempt: do not start anything else.
      if (generation !== playGeneration) return;
    }

    try {
      // Generous FIRST-attempt budget. Irrelevant on the blob path (memory resolves
      // instantly); it matters when this attempt is the network URL and may be
      // paying a cold-connection reconnect after the consent-statement idle.
      await playMp3(firstSrc, generation, MP3_LOAD_TIMEOUT_FIRST_MS);
      return;
    } catch {
      // mp3 missed/failed. If a newer prompt or stopAllAudio ran while we were
      // loading, this call is superseded — do NOT start the fallback voice.
      if (generation !== playGeneration) return;
      // One-shot retry BEFORE synthesis, ALWAYS against the origin URL — never the
      // blob. If the first attempt WAS the blob and still failed, the blob is the
      // suspect (revoked, or registered from a bad response), so replaying it would
      // fail identically; the network is the independent second source. If the first
      // attempt was already the origin URL, this is the original recovery: attempt 1
      // paid DNS/TCP/TLS, so attempt 2 often lands.
      try {
        // Tight RETRY budget: a second miss should reach the fallback quickly rather
        // than extend the subject's silence.
        await playMp3(mp3Url, generation, MP3_LOAD_TIMEOUT_RETRY_MS);
        return;
      } catch {
        // Second miss (or superseded mid-retry) — fall through to synthesis below.
        if (generation !== playGeneration) return;
      }
    }
  }
  // Re-check after the (possibly awaited) mp3 attempt: never start synthesis for a
  // prompt that has already been superseded.
  if (generation !== playGeneration) return;
  await speakViaSynthesis(text, generation, mp3Url ? "mp3-failed" : "no-mp3");
};

// Stops ALL prompt audio: the current mp3 (paused + detached) AND SpeechSynthesis,
// and advances the generation so any in-flight/late mp3 or synthesis recognizes it
// has been superseded — even if its handle was already released by a fired
// timeout. Use on teardown / stage transitions / before starting new audio.
export const stopAllAudio = () => {
  playGeneration += 1;
  if (currentMp3) {
    hardStop(currentMp3);
    currentMp3 = null;
  }
  // The Web Audio mirror of the currentMp3 hard-stop. stop() also fires the node's
  // onended, which resolves the pending playViaWebAudio promise rather than leaving
  // its caller hanging. A node that never started (or already ended) throws here —
  // swallowed, same best-effort contract as hardStop.
  if (currentSource) {
    try {
      currentSource.stop();
      currentSource.disconnect();
    } catch {
      // already stopped / never started — best effort.
    }
    currentSource = null;
  }
  if (typeof window.speechSynthesis !== "undefined") {
    window.speechSynthesis.cancel();
  }
};
